import {
    CouponSearchRequestModel,
    CouponListItemModel,
    CouponDetailInterface,
    CouponPlanScopeItemInterface,
    CreateCouponRequestModel,
    UpdateCouponRequestModel,
    CouponStatusEnum,
    CouponStatusTriggerEnum,
    CouponDetailModel,
    CouponDiscountTypeEnum,
    CouponUserEligibilityTypeEnum,
    CouponPaymentMethodEnum,
    CouponApplicableToTypeEnum,
    CurrencyTypesEnum,
    PortalsEnum,
    TradingPlanSearchResponseInterface,
    TradingPlanTypeEnum
} from '@spa/api';
import { ComponentTypesEnum, ColorCodesEnum, CouponPlanScopeTypeEnum } from '../../app/shared/enums';
import { FilterControlInterface } from '../../app/shared/interfaces';
import {
    DialogButtonTypesEnum,
    DialogConfigInterface,
    ReportTableColumn,
    ReportTableConfigInterface,
    ReportTableData
} from '../../app/shared/components';
import { SelectOptionInterface } from '../../app/shared/components/select/interfaces/select-option.interface';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { AmountMaxValidator, AmountMinValidator, OnlyDigitsValidator } from '@spa/validators';

import {
    AmountControl,
    AutocompleteControl,
    BaseControl,
    Helper,
    RadioGroupControl,
    SelectControl,
    SlideToggleControl
} from '../../app/shared/classes';
import { RadioGroupOptionInterface } from '../../app/shared/components/radio-group/interfaces/radio-group-option.interface';

// ── Selectors ──
export const PanelCouponsRequestIdSelector = 'id';
export const PanelCouponsStorageId = 'PanelCouponsComponent';

// The plan-scope pickers are not request fields of their own — the forms fold them into the
// `plan_scopes` collection — so they carry local control names.
export const CouponPlanScopeTradingPlansSelector = 'plan_scope_trading_plan_id';
export const CouponPlanScopePlanTypesSelector = 'plan_scope_plan_type_ids';
export const CouponPlanScopesFormArraySelector = 'plan_scope_trading_plans';
export const CouponPlanScopeMaxPlans = 20;

/**
 * Stands in for "no portal" in the portal picker. `PortalsEnum` starts at 1, so 0 is free — and the
 * backend rejects portal 0 outright, which is why it must never reach the request.
 */
export const CouponAllPortalsValue = 0;

/** Maps the portal control's value onto the optional `portal_id` the endpoints expect. */
export const CouponPortalIdValue = (value: number): number =>
    value === CouponAllPortalsValue || value == null ? null : value;

// ── Validation ──
export const CouponCodeMaxLength = 128;
export const CouponDiscountValueMax = 99999999.99;
export const CouponCodeValidatorKey = 'couponCode';
export const CouponPlanScopeValidatorKey = 'couponPlanScope';

/**
 * Bounds for the discount value, which depend on the discount type — the backend caps a percentage
 * at 100. Shared so the create and update forms stay in step when the type changes.
 */
export const CouponDiscountValueValidators = (discountType: number): ValidatorFn[] => [
    Validators.required,
    AmountMinValidator(0),
    AmountMaxValidator(discountType === CouponDiscountTypeEnum.Percentage ? 100 : CouponDiscountValueMax)
];

/**
 * Both redemption caps are optional whole numbers — an empty one means unlimited — and the backend
 * requires at least 1 when set. `app-amount-input [onlyIntegers]` blocks the decimal separator at
 * the keystroke; `OnlyDigitsValidator` covers a pasted value.
 */
export const CouponRedemptionCapValidators = (): ValidatorFn[] => [
    OnlyDigitsValidator(),
    AmountMinValidator(1)
];

/** Mirrors the backend rule: letters, digits, dashes and underscores only. */
export const CouponCodeValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
        return null;
    }

    return /^[A-Za-z0-9_-]+$/.test(control.value) ? null : { [CouponCodeValidatorKey]: true };
};

/**
 * Messages for the coupon-specific validator keys, merged over the shared form messages. Keys the
 * shared messages already cover are deliberately absent — they interpolate the limit reported by
 * the validator itself.
 */
export const CouponFormMessages = (controlLabel?: string): { [key: string]: string } => ({
    [CouponCodeValidatorKey]: `${controlLabel || 'The field'} may only contain letters, digits, dashes and underscores.`,
    [CouponPlanScopeValidatorKey]: 'Select at least one applicable plan or plan type.'
});

// ── Enum option builders ──
const CouponStatusOptions = (): SelectOptionInterface[] => [
    CouponStatusEnum.Inactive,
    CouponStatusEnum.Active,
    CouponStatusEnum.Expired
].map((status: number) => ({ label: Helper.textsHelper().getCouponStatusName(status), value: status }));

const CouponDiscountTypeOptions = (): SelectOptionInterface[] => [
    CouponDiscountTypeEnum.Percentage,
    CouponDiscountTypeEnum.FixedAmount,
    CouponDiscountTypeEnum.OverridePrice
].map((type: number) => ({ label: Helper.textsHelper().getCouponDiscountTypeName(type), value: type }));

const CouponPaymentMethodOptions = (): SelectOptionInterface[] => [
    CouponPaymentMethodEnum.All,
    CouponPaymentMethodEnum.Stripe,
    CouponPaymentMethodEnum.Crypto
].map((method: number) => ({ label: Helper.textsHelper().getCouponPaymentMethodName(method), value: method }));

const CouponUserEligibilityTypeOptions = (): SelectOptionInterface[] => [
    CouponUserEligibilityTypeEnum.AllPurchases,
    CouponUserEligibilityTypeEnum.FirstPurchase,
    CouponUserEligibilityTypeEnum.ReturningPurchase,
    CouponUserEligibilityTypeEnum.ChallengePassed
].map((type: number) => ({ label: Helper.textsHelper().getCouponUserEligibilityTypeName(type), value: type }));

const CouponApplicableToTypeOptions = (): SelectOptionInterface[] => [
    CouponApplicableToTypeEnum.All,
    CouponApplicableToTypeEnum.PlanPurchase,
    CouponApplicableToTypeEnum.RewindDailyLoss,
    CouponApplicableToTypeEnum.AutoPassDeactivation
].map((type: number) => ({ label: Helper.textsHelper().getCouponApplicableToTypeName(type), value: type }));

const CouponCurrencyOptions = (): SelectOptionInterface[] => [
    { label: CurrencyTypesEnum.Usd, value: CurrencyTypesEnum.Usd },
    { label: CurrencyTypesEnum.Euro, value: CurrencyTypesEnum.Euro }
];

/**
 * `portal_id` is optional — omitting it makes the coupon valid on every portal.
 *
 * "All portals" cannot be a `null` option: `MatSelect` treats null as a reset value and refuses to
 * select such an option, so it carries {@link CouponAllPortalsValue} and
 * {@link CouponPortalIdValue} maps it back to null for the request.
 */
const CouponPortalOptions = (): SelectOptionInterface[] => [
    { label: 'All portals', value: CouponAllPortalsValue },
    // ACE is the only portal redeeming coupons today. Add the others here as they come online.
    ...[
        PortalsEnum.AcePortal
    ].map((portalId: number) => ({ label: Helper.textsHelper().getPortalName(portalId), value: portalId }))
];

/**
 * `TradingPlanTypeEnum.All` is a filter-only member — a scope pinned to it resolves to no plan —
 * so it is left out of the plan-scope picker.
 */
const CouponPlanTypeOptions = (): SelectOptionInterface[] => [
    TradingPlanTypeEnum.Demo,
    TradingPlanTypeEnum.Tryout,
    TradingPlanTypeEnum.Tournament,
    TradingPlanTypeEnum.PrimeTrading,
    TradingPlanTypeEnum.FundedAccount
].map((type: number) => ({ label: Helper.textsHelper().getTradingPlanTypeName(type), value: type }));

export const CouponTradingPlanOptionLabel = (plan: TradingPlanSearchResponseInterface): string =>
    `${plan.name} (${plan.reference_id})`;

/**
 * One row of the applicable-plans list: an autocomplete that searches trading plans server-side.
 *
 * `asyncOptions` is assigned by {@link CouponPlanScopesForm}, which owns the service call. When
 * editing, `options` is seeded with the persisted plan so the input can render its name without a
 * lookup.
 */
export const CouponPlanScopePlanControl = (scope?: CouponPlanScopeItemInterface): AutocompleteControl =>
    new AutocompleteControl({
        name: CouponPlanScopeTradingPlansSelector,
        value: scope?.trading_plan_id ?? null,
        placeholder: 'Applicable Plan',
        validators: [],
        componentType: ComponentTypesEnum.Autocomplete,
        options: scope?.trading_plan_id != null
            ? [{ label: CouponPlanScopeLabel(scope), value: scope.trading_plan_id }]
            : [],
        async: true,
        removable: true
    });

// ── Plan scope <-> control value mapping ──
export const CouponPlanScopeSpecificPlans = (coupon: CouponDetailInterface): CouponPlanScopeItemInterface[] =>
    (coupon?.plan_scopes || [])
        .filter((scope: CouponPlanScopeItemInterface) =>
            scope.plan_scope_type === CouponPlanScopeTypeEnum.SpecificPlans && scope.trading_plan_id != null);

export const CouponPlanScopePlanTypeIds = (coupon: CouponDetailInterface): number[] =>
    (coupon?.plan_scopes || [])
        .filter((scope: CouponPlanScopeItemInterface) =>
            scope.plan_scope_type === CouponPlanScopeTypeEnum.AllPlansOfType && scope.trading_plan_type_id != null)
        .map((scope: CouponPlanScopeItemInterface) => scope.trading_plan_type_id);

// ── Display helpers ──
export const CouponPlanScopeLabel = (scope: CouponPlanScopeItemInterface): string => {
    if (scope?.plan_scope_type === CouponPlanScopeTypeEnum.AllPlansOfType) {
        return `All ${Helper.textsHelper().getTradingPlanTypeName(scope.trading_plan_type_id)} plans`;
    }

    return scope?.trading_plan_name || `Plan #${scope?.trading_plan_id}`;
};

// ── Status change ──

/**
 * The triggers that land a coupon on each status. `Active` has two: the backend distinguishes a
 * first activation from a return out of `Inactive`, so which one applies is decided by the
 * `availableTriggers` the API reports rather than by the target status alone.
 */
export const CouponStatusTriggers = (status: number): number[] => {
    switch (status) {
        case CouponStatusEnum.Active:
            return [CouponStatusTriggerEnum.Activate, CouponStatusTriggerEnum.Reactivate];
        case CouponStatusEnum.Inactive:
            return [CouponStatusTriggerEnum.Deactivate];
        case CouponStatusEnum.Expired:
            return [CouponStatusTriggerEnum.Expire];
        default:
            return [];
    }
};

/** The trigger to send for a target status, or null when the API does not currently allow it. */
export const CouponStatusTriggerFor = (status: number, availableTriggers: number[]): number => {
    const allowed = (availableTriggers || []);
    const match = CouponStatusTriggers(status).filter((trigger: number) => allowed.indexOf(trigger) >= 0);

    return match.length > 0 ? match[0] : null;
};

export const CouponStatusesControl = (): RadioGroupControl =>
    new RadioGroupControl({
        name: CouponDetailModel.getStatusSelector(),
        value: null,
        placeholder: 'Status',
        validators: [
            Validators.required
        ],
        componentType: ComponentTypesEnum.RadioGroup,
        invalid: false,
        errors: [],
        options: []
    });

export const CouponStatusControlOptions = (): RadioGroupOptionInterface[] => [
    CouponStatusEnum.Inactive,
    CouponStatusEnum.Active,
    CouponStatusEnum.Expired
].map((status: number) => ({
    label: Helper.textsHelper().getCouponStatusName(status),
    value: status,
    color: ColorCodesEnum.Primary
}));

/**
 * Pill colours follow the `status-*` language in `status.scss`: a coupon that cannot currently be
 * redeemed reads red, a live one green. `Expired` is terminal rather than wrong, so it stays grey.
 */
export const CouponStatusBadgeClass = (status: number): string => {
    switch (Number(status)) {
        case CouponStatusEnum.Active:
            return 'badge badge-pill badge-success coupon-status-badge-active';
        case CouponStatusEnum.Inactive:
            return 'badge badge-pill badge-danger';
        case CouponStatusEnum.Expired:
            return 'badge badge-pill badge-secondary';
        default:
            return 'badge badge-pill badge-secondary';
    }
};

/**
 * The coupon endpoints return every enum as its numeric value, so the report table needs the
 * label substituted before the row is rendered.
 */
export const PanelCouponsColumnFormatters: { [colId: string]: (value: any) => any } = {
    [CouponListItemModel.getStatusSelector()]: (value: number) =>
        Helper.textsHelper().getCouponStatusName(value),
    [CouponListItemModel.getDiscountTypeSelector()]: (value: number) =>
        Helper.textsHelper().getCouponDiscountTypeName(value),
    [CouponListItemModel.getPaymentMethodSelector()]: (value: number) =>
        Helper.textsHelper().getCouponPaymentMethodName(value),
    [CouponListItemModel.getUserEligibilityTypeSelector()]: (value: number) =>
        Helper.textsHelper().getCouponUserEligibilityTypeName(value),
    [CouponListItemModel.getApplicableToTypeSelector()]: (value: number) =>
        Helper.textsHelper().getCouponApplicableToTypeName(value)
};

// ── Filter Controls ──
export const PanelCouponsFilterControls = (): FilterControlInterface[] => [
    {
        name: CouponSearchRequestModel.getCodeSelector(),
        placeholder: 'Code',
        controls: [
            new BaseControl({
                name: CouponSearchRequestModel.getCodeSelector(),
                value: null,
                placeholder: 'Coupon code',
                validators: [],
                componentType: ComponentTypesEnum.Text
            })
        ],
        sortIndex: 1
    },
    {
        name: CouponSearchRequestModel.getStatusSelector(),
        placeholder: 'Status',
        controls: [
            new SelectControl({
                name: CouponSearchRequestModel.getStatusSelector(),
                value: null,
                placeholder: 'Status',
                validators: [],
                componentType: ComponentTypesEnum.Select,
                options: [
                    { label: 'All', value: null },
                    ...CouponStatusOptions()
                ]
            })
        ],
        sortIndex: 2
    },
    {
        name: CouponSearchRequestModel.getDiscountTypeSelector(),
        placeholder: 'Discount Type',
        controls: [
            new SelectControl({
                name: CouponSearchRequestModel.getDiscountTypeSelector(),
                value: null,
                placeholder: 'Discount Type',
                validators: [],
                componentType: ComponentTypesEnum.Select,
                options: [
                    { label: 'All', value: null },
                    ...CouponDiscountTypeOptions()
                ]
            })
        ],
        sortIndex: 3
    }
];

// ── Data Table Items (initial state) ──
export const PanelCouponsDataTableItems = (): ReportTableData[] => [
    new ReportTableData<string>({ colId: CouponListItemModel.getCodeSelector(),        value: null, hidden: false }),
    new ReportTableData<string>({ colId: CouponListItemModel.getCreatedAtSelector(),   value: null, hidden: false }),
    new ReportTableData<string>({ colId: CouponListItemModel.getStartDateTimeUtcSelector(), value: null, hidden: false }),
    new ReportTableData<string>({ colId: CouponListItemModel.getEndDateTimeUtcSelector(),   value: null, hidden: false }),
    new ReportTableData<string>({ colId: CouponListItemModel.getDiscountTypeSelector(),     value: null, hidden: false }),
    new ReportTableData<string>({ colId: CouponListItemModel.getDiscountValueSelector(),    value: null, hidden: false }),
    new ReportTableData<string>({ colId: CouponListItemModel.getCurrencyCodeSelector(),     value: null, hidden: false }),
    new ReportTableData<string>({ colId: CouponListItemModel.getPaymentMethodSelector(),    value: null, hidden: false }),
    new ReportTableData<string>({ colId: CouponListItemModel.getApplicableToTypeSelector(), value: null, hidden: false }),
    new ReportTableData<string>({ colId: CouponListItemModel.getRedeemedCountSelector(),    value: null, hidden: false }),
    new ReportTableData<string>({ colId: CouponListItemModel.getGlobalRedemptionCapSelector(),  value: null, hidden: false }),
    new ReportTableData<string>({ colId: CouponListItemModel.getPerUserRedemptionCapSelector(), value: null, hidden: false }),
    new ReportTableData<string>({ colId: CouponListItemModel.getStatusSelector(),               value: null, hidden: false }),
    new ReportTableData<string>({ colId: CouponListItemModel.getUserEligibilityTypeSelector(),  value: null, hidden: false }),
    new ReportTableData<string>({ colId: CouponListItemModel.getCreatedByUserNameSelector(),    value: null, hidden: false }),
];

// ── Data Table Config (column definitions) ──
export const PanelCouponsDataTableConfig = (): ReportTableConfigInterface => ({
    rows: [],
    columns: [
        new ReportTableColumn({ label: 'Code',            selector: CouponListItemModel.getCodeSelector(),            sortable: true }),
        new ReportTableColumn({ label: 'Created',         selector: CouponListItemModel.getCreatedAtSelector(),       sortable: true }),
        new ReportTableColumn({ label: 'Start',           selector: CouponListItemModel.getStartDateTimeUtcSelector(), sortable: true }),
        new ReportTableColumn({ label: 'End',             selector: CouponListItemModel.getEndDateTimeUtcSelector(),  sortable: true }),
        new ReportTableColumn({ label: 'Type',            selector: CouponListItemModel.getDiscountTypeSelector(),    sortable: true }),
        new ReportTableColumn({ label: 'Value',           selector: CouponListItemModel.getDiscountValueSelector(),   sortable: true }),
        new ReportTableColumn({ label: 'Currency',        selector: CouponListItemModel.getCurrencyCodeSelector(),    sortable: true }),
        new ReportTableColumn({ label: 'Payment',         selector: CouponListItemModel.getPaymentMethodSelector(),   sortable: true }),
        new ReportTableColumn({
        label: 'Applicable To',   selector: CouponListItemModel.getApplicableToTypeSelector(), sortable: true
    }),
        new ReportTableColumn({ label: 'Redeemed',        selector: CouponListItemModel.getRedeemedCountSelector(),   sortable: true }),
        new ReportTableColumn({ label: 'Redemption Cap',  selector: CouponListItemModel.getGlobalRedemptionCapSelector(), sortable: true }),
        new ReportTableColumn({
        label: 'Per-user Cap',    selector: CouponListItemModel.getPerUserRedemptionCapSelector(), sortable: true
    }),
        new ReportTableColumn({ label: 'Status',          selector: CouponListItemModel.getStatusSelector(),          sortable: true }),
        new ReportTableColumn({
        label: 'User Eligibility', selector: CouponListItemModel.getUserEligibilityTypeSelector(), sortable: true
    }),
        new ReportTableColumn({ label: 'Created By',      selector: CouponListItemModel.getCreatedByUserNameSelector(), sortable: true }),
    ],
    /**
     * Must stay empty. `report-table` builds its header checkboxes with `value: true` for every
     * column regardless of this list, so headers ignore it — but `ReportComponent`'s refresh path
     * hides the matching cells. Seeding it left the header row wider than the body after any
     * filter, which read as blank trailing columns. Operators can still hide columns via the cog.
     */
    hiddenColumnIds: [],
    actionMenu: false
});

// ── Create Coupon Dialog Config ──
export const CreateCouponDialogConfig = (): DialogConfigInterface => {
    return {
        process: {
            loading: false,
            finished: false,
            failed: false,
            failedMessage: '',
            succeedMessage: ''
        },
        buttons: [
            {
                type: DialogButtonTypesEnum.Cancel
            },
            {
                type: DialogButtonTypesEnum.Create
            }
        ],
        buttonsDisabled: false,
        buttonsHidden: false
    };
};

// ── Create Coupon Form Controls Interface ──
export interface CreateCouponFormControlsInterface {
    code: BaseControl;
    description: BaseControl;
    discountType: SelectControl;
    discountValue: AmountControl;
    currencyCode: SelectControl;
    paymentMethod: SelectControl;
    userEligibilityType: SelectControl;
    applicableToType: SelectControl;
    portalId: SelectControl;
    planScopePlanTypeIds: SelectControl;
    globalRedemptionCap: AmountControl;
    perUserRedemptionCap: AmountControl;
    startDateTimeUtc: BaseControl;
    endDateTimeUtc: BaseControl;
    activateImmediately: SlideToggleControl;
}

// ── Create Coupon Form Controls ──
export const CreateCouponControls = (): CreateCouponFormControlsInterface => ({
    code: new BaseControl({
        name: CreateCouponRequestModel.getCodeSelector(),
        value: '',
        placeholder: 'Code',
        validators: [Validators.required, Validators.maxLength(CouponCodeMaxLength), CouponCodeValidator],
        componentType: ComponentTypesEnum.Text
    }),
    description: new BaseControl({
        name: CreateCouponRequestModel.getDescriptionSelector(),
        value: '',
        placeholder: 'Description',
        validators: [],
        componentType: ComponentTypesEnum.Text
    }),
    discountType: new SelectControl({
        name: CreateCouponRequestModel.getDiscountTypeSelector(),
        value: CouponDiscountTypeEnum.Percentage,
        placeholder: 'Discount Type',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: CouponDiscountTypeOptions()
    }),
    discountValue: new AmountControl({
        name: CreateCouponRequestModel.getDiscountValueSelector(),
        value: null,
        placeholder: 'Discount Value',
        validators: CouponDiscountValueValidators(CouponDiscountTypeEnum.Percentage),
        componentType: ComponentTypesEnum.AmountInput,
        currency: null,
        isPercentage: true
    }),
    currencyCode: new SelectControl({
        name: CreateCouponRequestModel.getCurrencyCodeSelector(),
        value: null,
        placeholder: 'Currency',
        validators: [Validators.required],
        componentType: ComponentTypesEnum.Select,
        options: CouponCurrencyOptions()
    }),
    paymentMethod: new SelectControl({
        name: CreateCouponRequestModel.getPaymentMethodSelector(),
        value: CouponPaymentMethodEnum.All,
        placeholder: 'Payment Method',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: CouponPaymentMethodOptions(),
        disabled: true
    }),
    userEligibilityType: new SelectControl({
        name: CreateCouponRequestModel.getUserEligibilityTypeSelector(),
        value: CouponUserEligibilityTypeEnum.AllPurchases,
        placeholder: 'User Eligibility',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: CouponUserEligibilityTypeOptions()
    }),
    applicableToType: new SelectControl({
        name: CreateCouponRequestModel.getApplicableToTypeSelector(),
        value: CouponApplicableToTypeEnum.PlanPurchase,
        placeholder: 'Applicable To',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: CouponApplicableToTypeOptions()
    }),
    portalId: new SelectControl({
        name: CreateCouponRequestModel.getPortalIdSelector(),
        value: CouponAllPortalsValue,
        placeholder: 'Portal',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: CouponPortalOptions()
    }),
    planScopePlanTypeIds: new SelectControl({
        name: CouponPlanScopePlanTypesSelector,
        value: [],
        placeholder: 'Applicable Plan Types',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: CouponPlanTypeOptions(),
        multiple: true
    }),
    globalRedemptionCap: new AmountControl({
        name: CreateCouponRequestModel.getGlobalRedemptionCapSelector(),
        value: null,
        placeholder: 'Global Redemption Cap',
        validators: CouponRedemptionCapValidators(),
        componentType: ComponentTypesEnum.AmountInput,
        currency: null,
        isPercentage: false
    }),
    perUserRedemptionCap: new AmountControl({
        name: CreateCouponRequestModel.getPerUserRedemptionCapSelector(),
        value: null,
        placeholder: 'Per-User Redemption Cap',
        validators: CouponRedemptionCapValidators(),
        componentType: ComponentTypesEnum.AmountInput,
        currency: null,
        isPercentage: false
    }),
    startDateTimeUtc: new BaseControl({
        name: CreateCouponRequestModel.getStartDateTimeUtcSelector(),
        value: null,
        placeholder: 'Start Date',
        validators: [Validators.required],
        componentType: ComponentTypesEnum.Datepicker
    }),
    endDateTimeUtc: new BaseControl({
        name: CreateCouponRequestModel.getEndDateTimeUtcSelector(),
        value: null,
        placeholder: 'End Date',
        validators: [Validators.required],
        componentType: ComponentTypesEnum.Datepicker
    }),
    activateImmediately: new SlideToggleControl({
        name: CreateCouponRequestModel.getActivateImmediatelySelector(),
        value: false,
        checked: false,
        placeholder: 'Activate Immediately',
        validators: [],
        componentType: ComponentTypesEnum.SlideToggle,
        expectBooleanValue: true,
        color: ColorCodesEnum.Primary
    })
});

// ── Update Coupon Form Controls Interface ──
export interface UpdateCouponFormControlsInterface {
    code: BaseControl;
    description: BaseControl;
    discountType: SelectControl;
    discountValue: AmountControl;
    currencyCode: SelectControl;
    paymentMethod: SelectControl;
    userEligibilityType: SelectControl;
    applicableToType: SelectControl;
    portalId: SelectControl;
    planScopePlanTypeIds: SelectControl;
    globalRedemptionCap: AmountControl;
    perUserRedemptionCap: AmountControl;
    startDateTimeUtc: BaseControl;
    endDateTimeUtc: BaseControl;
}

// ── Update Coupon Form Controls ──
export const UpdateCouponControls = (coupon: CouponDetailInterface): UpdateCouponFormControlsInterface => ({
    code: new BaseControl({
        name: UpdateCouponRequestModel.getCodeSelector(),
        value: coupon?.code || '',
        placeholder: 'Code',
        validators: [Validators.required, Validators.maxLength(CouponCodeMaxLength), CouponCodeValidator],
        componentType: ComponentTypesEnum.Text
    }),
    description: new BaseControl({
        name: UpdateCouponRequestModel.getDescriptionSelector(),
        value: coupon?.description || '',
        placeholder: 'Description',
        validators: [],
        componentType: ComponentTypesEnum.Text
    }),
    discountType: new SelectControl({
        name: UpdateCouponRequestModel.getDiscountTypeSelector(),
        value: coupon?.discount_type ?? CouponDiscountTypeEnum.Percentage,
        placeholder: 'Discount Type',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: CouponDiscountTypeOptions()
    }),
    discountValue: new AmountControl({
        name: UpdateCouponRequestModel.getDiscountValueSelector(),
        value: coupon?.discount_value?.toString() ?? null,
        placeholder: 'Discount Value',
        validators: CouponDiscountValueValidators(coupon?.discount_type),
        componentType: ComponentTypesEnum.AmountInput,
        currency: null,
        isPercentage: coupon?.discount_type === CouponDiscountTypeEnum.Percentage
    }),
    currencyCode: new SelectControl({
        name: UpdateCouponRequestModel.getCurrencyCodeSelector(),
        value: coupon?.currency_code || null,
        placeholder: 'Currency',
        validators: [Validators.required],
        componentType: ComponentTypesEnum.Select,
        options: CouponCurrencyOptions()
    }),
    paymentMethod: new SelectControl({
        name: UpdateCouponRequestModel.getPaymentMethodSelector(),
        value: coupon?.payment_method ?? CouponPaymentMethodEnum.All,
        placeholder: 'Payment Method',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: CouponPaymentMethodOptions(),
        disabled: true
    }),
    userEligibilityType: new SelectControl({
        name: UpdateCouponRequestModel.getUserEligibilityTypeSelector(),
        value: coupon?.user_eligibility_type ?? CouponUserEligibilityTypeEnum.AllPurchases,
        placeholder: 'User Eligibility',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: CouponUserEligibilityTypeOptions()
    }),
    applicableToType: new SelectControl({
        name: UpdateCouponRequestModel.getApplicableToTypeSelector(),
        value: coupon?.applicable_to_type ?? CouponApplicableToTypeEnum.PlanPurchase,
        placeholder: 'Applicable To',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: CouponApplicableToTypeOptions()
    }),
    portalId: new SelectControl({
        name: UpdateCouponRequestModel.getPortalIdSelector(),
        value: coupon?.portal_id ?? CouponAllPortalsValue,
        placeholder: 'Portal',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: CouponPortalOptions()
    }),
    planScopePlanTypeIds: new SelectControl({
        name: CouponPlanScopePlanTypesSelector,
        value: CouponPlanScopePlanTypeIds(coupon),
        placeholder: 'Applicable Plan Types',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: CouponPlanTypeOptions(),
        multiple: true
    }),
    globalRedemptionCap: new AmountControl({
        name: UpdateCouponRequestModel.getGlobalRedemptionCapSelector(),
        value: coupon?.global_redemption_cap?.toString() ?? null,
        placeholder: 'Global Redemption Cap',
        validators: CouponRedemptionCapValidators(),
        componentType: ComponentTypesEnum.AmountInput,
        currency: null,
        isPercentage: false
    }),
    perUserRedemptionCap: new AmountControl({
        name: UpdateCouponRequestModel.getPerUserRedemptionCapSelector(),
        value: coupon?.per_user_redemption_cap?.toString() ?? null,
        placeholder: 'Per-User Redemption Cap',
        validators: CouponRedemptionCapValidators(),
        componentType: ComponentTypesEnum.AmountInput,
        currency: null,
        isPercentage: false
    }),
    startDateTimeUtc: new BaseControl({
        name: UpdateCouponRequestModel.getStartDateTimeUtcSelector(),
        value: coupon?.start_date_time_utc || null,
        placeholder: 'Start Date',
        validators: [Validators.required],
        componentType: ComponentTypesEnum.Datepicker
    }),
    endDateTimeUtc: new BaseControl({
        name: UpdateCouponRequestModel.getEndDateTimeUtcSelector(),
        value: coupon?.end_date_time_utc || null,
        placeholder: 'End Date',
        validators: [Validators.required],
        componentType: ComponentTypesEnum.Datepicker
    })
});

// ── Redemption History Data Table ──
export const PanelCouponRedemptionsDataTableConfig = (): ReportTableConfigInterface => ({
    rows: [],
    columns: [
        new ReportTableColumn({ label: 'Product Type',    selector: 'product_type',    sortable: true }),
        new ReportTableColumn({ label: 'Product ID',      selector: 'product_id',      sortable: true }),
        new ReportTableColumn({ label: 'Redemption Date', selector: 'redemption_date',  sortable: true }),
        new ReportTableColumn({ label: 'Discount Value',  selector: 'discount_value',   sortable: true }),
        new ReportTableColumn({ label: 'Currency',        selector: 'discount_currency', sortable: true }),
        new ReportTableColumn({ label: 'Discount %',      selector: 'discount_percentage', sortable: true }),
        new ReportTableColumn({ label: 'User ID',         selector: 'user_id',          sortable: true }),
        new ReportTableColumn({ label: 'User Email',      selector: 'user_email',       sortable: true })
    ],
    hiddenColumnIds: [],
    actionMenu: false
});
